import { Controller } from '@hotwired/stimulus';
import { dropdownMaxHeightPx } from '../../utils/dropdown_max_height';

export default class extends Controller {
  static targets = [
    'wrapper', 'button', 'buttonLabel', 'hidden',
    'checkmark', 'helper', 'dropdown',
    'options', 'optionTemplate'
  ]
  static values = {
    direction: { type: String, default: 'auto' }, // 'auto' | 'bottom' | 'top'
    minWidth: Number,
    maxWidth: Number,
    frontendValidation: { type: Boolean, default: false },
    submitOnSelect: { type: Boolean, default: false },
  };

  connect() {
    this.refreshBoundActions = this.refreshBoundActions.bind(this)
    document.addEventListener("turbo:frame-load", this.refreshBoundActions)

    const dd = this.element.querySelector('[data-ss-dropdown]');
    if (!dd) return;
    this.dropdownEl = dd.cloneNode(true);
    this.dropdownOriginParent = dd.parentElement;
    this.portalRoot = this._ensurePortalRoot();
    this._syncFromDOM();

    const hasError = this.hiddenTarget.dataset.hasError === 'true';
    if (hasError) this.showError();

    if (this.frontendValidationValue) {
      this.addValidationListener();
    }
  }

  addValidationListener() {
    const form = this.element.closest('form');
    if (form) {
      form.addEventListener('submit', (event) => {
        const isRequired = this.buttonTarget.dataset.required === 'true';
        const value = this.hasHiddenTarget ? this.hiddenTarget.value : '';

        if (isRequired && (!value || value.trim() === '')) {
          event.preventDefault();
          event.stopPropagation();
          this.showValidationError();
          this.buttonTarget.focus();
        }
      });
    }
  }

  showValidationError() {
    this.showError();

    if (!this.hasHelperTarget) {
      const helperText = document.createElement('p');
      helperText.dataset.singleSelectFieldComponentWithSearchTarget = 'helper';
      helperText.className = 'mt-8 text-paragraph-s text-red-1000';
      const label = this.element.querySelector('label');
      const fieldName = label ? label.textContent.trim().replace(/\s*\*\s*$/, '') : 'Field';
      helperText.textContent = `${fieldName} can't be blank`;
      this.element.appendChild(helperText);
    } else {
      this.helperTarget.style.display = '';
      this.helperTarget.classList.add('text-red-1000');
    }
  }

  clearValidationError() {
    if (this.buttonTarget) {
      this.buttonTarget.classList.remove('border-red-300', 'text-red-1000', 'placeholder-red-1000', 'has-error');
      this.buttonTarget.classList.add('border-black-100');
    }

    if (this.hasHelperTarget && !this.helperTarget.dataset.backendError) {
      this.helperTarget.style.display = 'none';
    }
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.refreshBoundActions)

    this._teardownOutside();
    this._detachObservers();
    this._unbindDropdownHandlers();
    if (this.dropdownEl && this.dropdownEl.parentElement === this.portalRoot) {
      this.dropdownOriginParent.appendChild(this.dropdownEl);
    }
  }

  refreshBoundActions(event) {
    const frame = event.target
    if (this.element.contains(frame)) {
      this.application.load()
    }
  }

  // === API ===
  toggle(e) {
    e?.preventDefault();
    e?.stopPropagation();
    this.isDropdownOpen ? this.close() : this.open();
  }

  open() {
    if (this.isDropdownOpen) return;

    // teleport
    this.dropdownEl.innerHTML = this.dropdownTarget.innerHTML
    this.portalRoot.appendChild(this.dropdownEl);

    this.dropdownEl.classList.remove('hidden');
    this.dropdownEl.classList.add('invisible', 'ss-transition-none', 'opacity-0', 'scale-95');

    // a11y
    this.buttonTarget.setAttribute('aria-expanded', 'true');

    // position
    this._positionDropdown();

    // bind after teleport
    this._bindDropdownHandlers();
    this._grabCommonRefs(); // _searchEl, _listEl, _clearBtnEl, _emptyEl

    // reset search + hide "No results"
    if (this._emptyEl) this._emptyEl.hidden = true;
    if (this._searchEl) {
      this._searchEl.value = '';
      this._toggleClearBtn(false);
      this._filter('');
      try {
        this._searchEl.focus({ preventScroll: true });
      } catch (_) {}
    }

    const currentSelected = this.hasHiddenTarget ? (this.hiddenTarget.value || '').trim() : '';
    this._updateCheckmarks(currentSelected);

    // animate
    void this.dropdownEl.offsetHeight;
    this.dropdownEl.classList.remove('ss-transition-none', 'invisible');
    requestAnimationFrame(() => {
      this.dropdownEl.classList.remove('opacity-0', 'scale-95');
      this.dropdownEl.classList.add('opacity-100', 'scale-100');
    });

    this._setupOutside();
    this._attachObservers();
    this.isDropdownOpen = true;
  }

  close() {
    if (!this.isDropdownOpen) return;

    this.dropdownEl.classList.add('opacity-0', 'scale-95');
    this.dropdownEl.classList.remove('opacity-100', 'scale-100');

    const onEnd = () => {
      this.dropdownEl.classList.add('hidden');
      this.dropdownEl.removeEventListener('transitionend', onEnd);
    };
    this.dropdownEl.addEventListener('transitionend', onEnd);

    this.buttonTarget.setAttribute('aria-expanded', 'false');

    this._unbindDropdownHandlers();
    this._teardownOutside();
    this._detachObservers();
    this.isDropdownOpen = false;
  }

  // === Selection (click on <li>) ===
  selectFromItem(event) {
    const t = event?.target || null;
    const li = t?.closest?.('li[data-value]') || null;
    if (!li) return;
    this._handleLiClick(li);
  }

  // === Selection (from outside) ===
  selectItem(value) {
    const list = this.optionsTarget
    const item = list.querySelector(`[data-value="${value}"]`)
    if (item) this._applySelection(item.dataset.label, value)
  }

  refreshOptions(data) {
    this.clearSelection()
    this.optionsTarget.innerHTML = ''

    data.forEach((option) => {
      const clone = this.optionTemplateTarget.content.cloneNode(true)
      const label = clone.querySelector("span")
      const li = clone.querySelector("li")

      label.textContent = option.name
      li.dataset.value = option.id
      li.dataset.label = option.name

      this.optionsTarget.appendChild(clone)
    })
  }

  showError() {
    if (this.buttonTarget) {
      this.buttonTarget.classList.add('border-red-300');
      this.buttonTarget.classList.add('text-red-1000');
      this.buttonTarget.classList.add('placeholder-red-1000');
      this.buttonTarget.classList.add('has-error');
    }

    if (this.hasHelperTarget) {
      this.helperTarget.classList.add('text-red-1000');
    }
  }

  clearSelection() {
    const placeholder = this.buttonTarget.dataset.placeholder || '';
    this._updateValue(placeholder, '');
    this._updateCheckmarks('');
    this.close();
  }

  submitForm() {
    const form = this.element.closest('form');
    if (form) {
      form.requestSubmit();
    }
  }

  _handleLiClick(li) {
    const value = (li?.dataset.value || '').trim();
    const labelEl = li?.querySelector('[data-label]') || li?.querySelector('span');
    const label = (li?.dataset.label || labelEl?.textContent || value).trim();
    this._applySelection(label, value);
  }

  _applySelection(label, value) {
    this._updateValue(label, value);
    this._updateCheckmarks(value);
    if (this.frontendValidationValue) {
      this.clearValidationError();
    }
    this.close();
    if (this.submitOnSelectValue) {
      this.submitForm();
    }
  }

  _updateValue(label, rawValue) {
    if (this.hasButtonLabelTarget) this.buttonLabelTarget.textContent = label || '';
    else this.buttonTarget.innerText = label || '';

    const ph = this.buttonTarget.dataset.placeholder || '';
    const isPlaceholder = !rawValue || label === ph;
    this.buttonTarget.classList.toggle('text-black-400', !!isPlaceholder);
    this.buttonTarget.classList.toggle('text-black-1000', !isPlaceholder);

    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = rawValue;
      this.hiddenTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  _syncFromDOM() {
    const currentLabel = this.hasButtonLabelTarget
      ? (this.buttonLabelTarget?.textContent || '').trim()
      : this.buttonTarget.innerText.trim();

    const placeholder = this.buttonTarget.dataset.placeholder || '';
    const isPlaceholder = currentLabel === placeholder;
    
    if (this.hasHiddenTarget && !this.hiddenTarget.value) {
      this.hiddenTarget.value = isPlaceholder ? '' : this._valueForLabel(currentLabel);
    }
    this._updateCheckmarks(this.hasHiddenTarget ? this.hiddenTarget.value : '');

    this.buttonTarget.classList.toggle('text-black-400', !!isPlaceholder);
    this.buttonTarget.classList.toggle('text-black-1000', !isPlaceholder);
  }

  _valueForLabel(label) {
    if (!this.dropdownEl || !label) return '';

    const options = this.dropdownEl.querySelectorAll('li[role="option"][data-value]');
    const match = Array.from(options).find((li) => (li.dataset.label || '').trim() === label.trim());
    return match ? (match.dataset.value || '').trim() : '';
  }

  // === Checkmarks ===
  _updateCheckmarks(selectedValue) {
    if (!this.dropdownEl) return;

    const value = String(selectedValue || '').trim();
    const containers = this.dropdownEl.querySelectorAll(
      '[data-single-select-field-component-with-search-target="checkmark"]',
    );
    const tpl = this.element.querySelector('#check-icon-template');

    containers.forEach((container) => {
      const li = container.closest('li');
      const key = (li?.dataset.value || '').trim();

      container.innerHTML = '';

      if (value && key === value) {
        let node = null;
        if (tpl?.content?.firstElementChild) {
          node = tpl.content.firstElementChild.cloneNode(true);
        } else {
          const el = document.createElement('div');
          el.className = 'rounded-full w-20 h-20 bg-black-1000 flex items-center justify-center';
          el.innerHTML = '<span class="text-white">✓</span>';
          node = el;
        }
        container.appendChild(node);
        li?.setAttribute('aria-selected', 'true');
      } else {
        li?.setAttribute('aria-selected', 'false');
      }
    });
  }

  // === Bind after teleport (options + search) ===
  _bindDropdownHandlers() {
    if (this._dropdownHandlersBound || !this.dropdownEl) return;

    // click/change in list
    this._onDropdownChange = (e) => {
      const input = e?.target?.closest?.('input[type="radio"]');
      if (input && this.dropdownEl.contains(input)) {
        const value = (input.value || '').trim();
        const label = (
          (input.dataset && input.dataset.label) ||
          (input.closest('label') ? input.closest('label').textContent : '') ||
          value
        ).trim();
        this._applySelection(label, value); // _updateCheckmarks
      }
    };

    this._onDropdownClick = (e) => {
      const li = e?.target?.closest?.('li[data-value]');
      if (li && this.dropdownEl.contains(li)) {
        e.preventDefault();
        e.stopPropagation();
        this._handleLiClick(li); // _updateCheckmarks
        return;
      }
      const clearBtn = e?.target?.closest?.('[data-action*="clearSelection"]');
      if (clearBtn && this.dropdownEl.contains(clearBtn)) {
        e.preventDefault();
        e.stopPropagation();
        this.clearSelection();
      }
    };

    this.dropdownEl.addEventListener('change', this._onDropdownChange);
    this.dropdownEl.addEventListener('click', this._onDropdownClick);

    this._grabCommonRefs();

    // handlers for search
    if (this._searchEl) {
      this._onSearchInput = () => {
        const q = this._normalize(this._searchEl.value || '');
        this._toggleClearBtn(q.length > 0);
        this._filter(q); // real filtering
        this._positionDropdown();
      };
      this._onSearchKeydown = (e) => {
        if (!e) return;
        if (e.key === 'Escape') {
          if (this._searchEl.value) {
            this._searchEl.value = '';
            this._toggleClearBtn(false);
            this._filter('');
            this._positionDropdown();
          } else {
            this.close();
          }
          e.preventDefault();
        } else if (e.key === 'Enter') {
          const visible = this._visibleOptions();
          if (visible.length === 1) {
            this._handleLiClick(visible[0]); // _updateCheckmarks
            e.preventDefault();
          }
        }
      };

      this._searchEl.addEventListener('input', this._onSearchInput);
      this._searchEl.addEventListener('keydown', this._onSearchKeydown);
    }

    if (this._clearBtnEl) {
      this._onClearClick = (e) => {
        e?.preventDefault();
        e?.stopPropagation();
        if (!this._searchEl) return;
        this._searchEl.value = '';
        this._toggleClearBtn(false);
        this._filter('');
        try {
          this._searchEl.focus({ preventScroll: true });
        } catch (_) {}
        this._positionDropdown();
      };
      this._clearBtnEl.addEventListener('click', this._onClearClick);
    }

    this._dropdownHandlersBound = true;
  }

  _grabCommonRefs() {
    this._searchEl = this.dropdownEl.querySelector(
      '[data-single-select-with-search-target="search"]',
    );
    this._listEl = this.dropdownEl.querySelector('[data-single-select-with-search-target="list"]');
    this._clearBtnEl = this.dropdownEl.querySelector(
      '[data-single-select-with-search-target="clearSearchBtn"]',
    );
    this._emptyEl =
      this.dropdownEl.querySelector('[data-ss-empty]') ||
      this.dropdownEl.querySelector('[data-single-select-with-search-target-empty]');
  }

  _unbindDropdownHandlers() {
    if (!this._dropdownHandlersBound || !this.dropdownEl) return;

    this.dropdownEl.removeEventListener('change', this._onDropdownChange);
    this.dropdownEl.removeEventListener('click', this._onDropdownClick);

    if (this._searchEl) {
      this._searchEl.removeEventListener('input', this._onSearchInput);
      this._searchEl.removeEventListener('keydown', this._onSearchKeydown);
    }
    if (this._clearBtnEl) {
      this._clearBtnEl.removeEventListener('click', this._onClearClick);
    }

    this._onDropdownChange = null;
    this._onDropdownClick = null;
    this._onSearchInput = null;
    this._onSearchKeydown = null;
    this._onClearClick = null;

    this._dropdownHandlersBound = false;
  }

  _toggleClearBtn(show) {
    if (this._clearBtnEl) this._clearBtnEl.classList.toggle('hidden', !show);
  }

  _normalize(str) {
    return (str || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  _filter(q) {
    const list = this._listEl || this.dropdownEl;
    const items = list.querySelectorAll('li[role="option"][data-value]');
    const qq = this._normalize(q);
    let visible = 0;

    for (let i = 0; i < items.length; i++) {
      const li = items[i];
      const labelAttr = li.getAttribute('data-label') || '';
      const textNode = li.querySelector('[data-label]') || li;
      const raw = labelAttr || (textNode ? textNode.textContent : '') || '';
      const text = this._normalize(raw);
      const match = !qq || text.includes(qq);

      li.hidden = !match;

      if (match) visible += 1;
    }

    if (this._emptyEl) this._emptyEl.hidden = !(qq && visible === 0);
  }

  _visibleOptions() {
    const list = this._listEl || this.dropdownEl;
    const all = list.querySelectorAll('li[role="option"][data-value]');
    const out = [];
    for (let i = 0; i < all.length; i++) {
      const li = all[i];
      if (!li.hidden) out.push(li);
    }
    return out;
  }

  // === Portal ===
  _ensurePortalRoot() {
    let root = document.getElementById('ui-layer-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'ui-layer-root';
      root.classList.add('ui-layer-root');
      root.setAttribute('data-turbo-permanent', '');
      document.body.appendChild(root);
    }
    return root;
  }

  // === Positioning ===
  _positionDropdown() {
    const btnRect = this.buttonTarget.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const M = 8;

    const minW = this.hasMinWidthValue ? this.minWidthValue : 200;
    const maxW = this.hasMaxWidthValue ? this.maxWidthValue : Infinity;
    const width = Math.max(minW, Math.min(btnRect.width, maxW));

    this.dropdownEl.classList.add('ss-no-max-h');
    const naturalH = this.dropdownEl.offsetHeight || 300;
    this.dropdownEl.classList.remove('ss-no-max-h');

    const spaceBelow = vh - btnRect.bottom - M;
    const spaceAbove = btnRect.top - M;
    const wantsTop = this.directionValue === 'top';
    const wantsBottom = this.directionValue === 'bottom';
    const openUp = wantsTop
      ? true
      : wantsBottom
        ? false
        : spaceBelow < Math.min(naturalH, 320) && spaceAbove > spaceBelow;

    let top = openUp ? Math.max(M, btnRect.top - Math.min(naturalH, spaceAbove)) : btnRect.bottom;
    const maxH = openUp ? spaceAbove : spaceBelow;

    let left = btnRect.left;
    if (left + width + M > vw) left = Math.max(M, vw - width - M);
    if (left < M) left = M;

    this._setVar('--ss-left', `${Math.round(left)}px`);
    this._setVar('--ss-top', `${Math.round(top)}px`);
    this._setVar('--ss-width', `${Math.round(width)}px`);
    this._setVar('--ss-max-h', `${dropdownMaxHeightPx(naturalH, maxH)}px`);
  }

  _setVar(name, value) {
    this.dropdownEl.style.setProperty(name, value);
  }

  // === Outside / Esc ===
  _setupOutside() {
    if (this.outsideBound) return;

    this._onDocPointerDown = (e) => {
      const t = e?.target;
      const insideDropdown = t && this.dropdownEl.contains(t);
      const insideComponent = t && this.element.contains(t);
      if (!insideDropdown && !insideComponent) this.close();
    };
    this._onDocKeydown = (e) => {
      if (e?.key === 'Escape') this.close();
    };

    document.addEventListener('pointerdown', this._onDocPointerDown, true);
    document.addEventListener('keydown', this._onDocKeydown);
    this.outsideBound = true;
  }

  _teardownOutside() {
    if (!this.outsideBound) return;
    document.removeEventListener('pointerdown', this._onDocPointerDown, true);
    document.removeEventListener('keydown', this._onDocKeydown);
    this.outsideBound = false;
  }

  // === Relayout ===
  _attachObservers() {
    const self = this;
    this._reposition = function () {
      if (self.isDropdownOpen) self._positionDropdown();
    };
    this._onScroll = function (e) {
      if (self.dropdownEl && self.dropdownEl.contains(e.target)) return;
      self._reposition();
    };
    window.addEventListener('scroll', this._onScroll, true);
    window.addEventListener('resize', this._reposition);

    this.ro = new ResizeObserver(this._reposition);
    this.ro.observe(this.buttonTarget);

    if (window.visualViewport) {
      this.vvo = { onResize: this._reposition, onScroll: this._reposition };
      window.visualViewport.addEventListener('resize', this.vvo.onResize);
      window.visualViewport.addEventListener('scroll', this.vvo.onScroll);
    }
  }

  _detachObservers() {
    window.removeEventListener('scroll', this._onScroll, true);
    window.removeEventListener('resize', this._reposition);
    this.ro?.disconnect();
    this.ro = undefined;
    if (this.vvo) {
      window.visualViewport.removeEventListener('resize', this.vvo.onResize);
      window.visualViewport.removeEventListener('scroll', this.vvo.onScroll);
      this.vvo = undefined;
    }
  }
}
